import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Button, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { ChildTileData, CMSViewMode } from '../../types/CMSTypes';
import { getCategoryDetails, buildCategoryUrl } from '../../api/cmsApi';

const MAX_COLLECTIONS = 12;
const GAP_PX = 16;
const SLIDE_MS = 500;

interface CMSConsumerNewArrivalsGridProps {
  newArrivalsData: ChildTileData[];
  header?: string;
  hasTitles?: boolean;
  hasButtons?: boolean;
  hasBackgroundText?: boolean;
  spacingValue?: number;
  marginTop?: string | number;
  marginBottom?: string | number;
  viewMode?: CMSViewMode;
}

const ChevronIcon: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d={direction === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CMSConsumerNewArrivalsGrid: React.FC<CMSConsumerNewArrivalsGridProps> = ({
                                                                                 newArrivalsData,
                                                                                 header = "New arrivals",
                                                                                 hasTitles = true,
                                                                                 hasButtons = true,
                                                                                 hasBackgroundText = false,
                                                                                 spacingValue = 4,
                                                                                 marginTop = '5rem',
                                                                                 marginBottom = '5rem',
                                                                                 viewMode = 'live'
                                                                               }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Handle navigation for collection items
  const handleExploreClick = async (item: ChildTileData) => {
    try {
      // Build the collection URL using the same logic as carousel banner
      const categoryResponse = await getCategoryDetails(item.category_id, viewMode);
      if (categoryResponse.success && categoryResponse.cms_category) {
        const categoryUrl = buildCategoryUrl(categoryResponse.cms_category);
        navigate(categoryUrl);
      } else {
        // Fallback to manual URL building
        if (item.item_group && item.application && item.collection) {
          const url = `/item_group/${item.item_group}/application/${encodeURIComponent(item.application)}/collection/${encodeURIComponent(item.collection)}`;
          navigate(url);
        }
      }
    } catch (error) {
      console.error('Error navigating to collection:', error);
      // Final fallback - use the application/collection structure
      if (item.item_group && item.application && item.collection) {
        const url = `/item_group/${item.item_group}/application/${encodeURIComponent(item.application)}/collection/${encodeURIComponent(item.collection)}`;
        navigate(url);
      }
    }
  };

  // Slides visible per breakpoint: xs = 1, sm = 2, md+ = 12 / spacingValue (4 -> 3)
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const visibleCount = isMd
    ? Math.max(1, Math.floor(12 / (spacingValue || 4)))
    : isSm
      ? 2
      : 1;

  // Cap at 12 collections
  const items: ChildTileData[] = useMemo(
    () => (Array.isArray(newArrivalsData) ? newArrivalsData.slice(0, MAX_COLLECTIONS) : []),
    [newArrivalsData]
  );
  const count = items.length;

  // Only cycle when there are more slides than fit on screen
  const canCycle = count > visibleCount;

  // Three copies so the loop is seamless both ways.
  const loopedItems = useMemo(
    () => (canCycle ? [...items, ...items, ...items] : items),
    [items, canCycle]
  );

  const [index, setIndex] = useState(count);
  const [isAnimating, setIsAnimating] = useState(false);
  const [instant, setInstant] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  // Reset to the middle copy when data or layout changes
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setInstant(true);
    setIsAnimating(false);
    setIndex(canCycle ? count : 0);
  }, [count, canCycle, visibleCount]);

  // Re-enable the transition a couple of frames after an instant jump
  useEffect(() => {
    if (!instant) return;
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInstant(false))
    );
    return () => cancelAnimationFrame(id);
  }, [instant]);

  const slide = (step: 1 | -1) => {
    if (!canCycle || isAnimating || instant) return;
    const next = index + step;
    setIsAnimating(true);
    setIndex(next);
    timerRef.current = setTimeout(() => {
      setIsAnimating(false);
      if (next >= count * 2) {
        setInstant(true);
        setIndex(next - count);
      } else if (next < count) {
        setInstant(true);
        setIndex(next + count);
      }
    }, SLIDE_MS + 50);
  };

  // Show missing content if no data
  if (!newArrivalsData || newArrivalsData.length === 0) {
    return <MissingContent sectionName="New Arrivals" />;
  }

  const slideWidth = `calc((100% - ${GAP_PX * (visibleCount - 1)}px) / ${visibleCount})`;
  const translateX = `calc(${-index} * (100% + ${GAP_PX}px) / ${visibleCount})`;

  return (
    <Box sx={{ mt: marginTop, mb: marginBottom }}>
      {/* Header + cycle buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          mx: { xs: 0, lg: '4rem' },
          px: { xs: '5%', lg: 0 },
        }}
      >
        <Typography
          fontWeight="300"
          variant="h3"
          sx={{
            fontSize: { xs: '44px', lg: '56px' },
          }}
        >
          {header}
        </Typography>

        {canCycle && (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <IconButton
              aria-label="Previous new arrivals"
              onClick={() => slide(-1)}
              sx={{ color: '#000' }}
            >
              <ChevronIcon direction="left" />
            </IconButton>
            <IconButton
              aria-label="Next new arrivals"
              onClick={() => slide(1)}
              sx={{ color: '#000' }}
            >
              <ChevronIcon direction="right" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Carousel viewport */}
      <Box sx={{ overflow: 'hidden', marginTop: '30px' }}>
        <Box
          sx={{
            display: 'flex',
            gap: `${GAP_PX}px`,
            transform: `translateX(${translateX})`,
            transition: instant ? 'none' : `transform ${SLIDE_MS}ms ease`,
          }}
        >
          {loopedItems.map((item, i) => (
            <Box
              key={`${item.category_id ?? 'item'}-${i}`}
              sx={{ flex: `0 0 ${slideWidth}`, minWidth: 0 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: {
                    xs: 'auto',
                    md: '650px',
                  },
                }}
              >
                {/* Image */}
                <img
                  src={item.tile_display_media_single_link || '/placeholder-image.jpg'}
                  style={{
                    width: '100%',
                    objectFit: 'cover',
                    height: '100%',
                    display: 'block',
                  }}
                  alt={item.collection || item.category_name || 'New Arrival'}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />

                {/* Background text overlay */}
                {hasBackgroundText && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: 'center',
                        color: '#fff',
                        fontSize: '2rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      {item.collection || item.category_name}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Title and Button */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginTop: '1.5rem',
                }}
              >
                {hasTitles && (
                  <Typography
                    variant="h5"
                    fontSize={'40px'}
                    fontWeight={'400'}
                  >
                    {item.collection || item.category_name}
                  </Typography>
                )}

                {hasButtons && (
                  <Button
                    sx={{
                      margin: '1rem 0rem',
                      borderRadius: '1px',
                      padding: { xs: '1rem 3rem', lg: '24px' },
                      width: { xs: 'auto', lg: '280px' },
                      border: { xs: '', lg: '1px solid #000' },
                    }}
                    variant="outlined"
                    color="primary"
                    onClick={() => handleExploreClick(item)}
                  >
                    Explore More
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default CMSConsumerNewArrivalsGrid;
